import React, { FC, ReactElement, Suspense } from "react"
import { Settings as SettingsIcon } from "react-feather"
import { Menu, Company, User } from "../Menu/Menu"
import { useTokenData } from "../../hooks/useTokenData"
import { useUsersInfo } from "../../pages/contracts-view/hooks/useUsersInfo"
import { GdprConsents } from "../../components/GdprConsents"
import { Loader } from "../../components/Loader"
import { ROUTES } from "../../consts/routes"
import { getInitials } from "../../utils/getInitials"
import { UserNotAllowed } from "../UserNotAllowed"
import Footer from "../Footer"
import style from "./PageLayout.module.scss"
import { PrivacyPolicy } from "../PrivacyPolicy"
import { SkipLink } from "../SkipLink"

type PageLayoutProps = {
  children: (user: User) => ReactElement
}

type PageContainerProps = {
  children: React.ReactNode
}

export const PageContainer: FC<PageContainerProps> = ({ children }) => {
  return <div className={style.page}>{children}</div>
}

export const PageLayout: FC<PageLayoutProps> = ({ children }) => {
  const { data } = useTokenData()
  const { data: userInfo, loading: loadingUserInfo } = useUsersInfo()

  if (!data.userToken || loadingUserInfo) return <Loader fullPage />

  const isRoleFuturo = data?.roleFuturo ?? false
  const isRoleMediatori = data?.roleMediatori ?? false
  const isDashboardEnabled = isRoleFuturo && !isRoleMediatori

  const vatCode = data.companyVatCode ?? ""
  const firstName = userInfo?.user.firstName ?? data.firstName ?? ""
  const lastName = userInfo?.user.lastName ?? data.lastName ?? ""
  const companyName = userInfo?.user.companyName ?? data.companyName ?? ""
  const mobile = userInfo?.user.mobile ?? ""

  const userCompanies: Company = { vatCode, name: companyName, active: true }

  const user: User = {
    name: firstName ?? "",
    initials: getInitials(`${firstName} ${lastName}`),
    fullName: `${firstName} ${lastName}`,
    companies: [userCompanies],
    email: data.email,
    mobile,
    data
  }

  const secondaryMenu = [
    {
      text: "Gestione profilo",
      url: ROUTES.profile,
      icon: <SettingsIcon size={24} aria-hidden="true" />
    }
  ]

  const primaryMenu = [
    {
      text: "Dashboard",
      active: true,
      external: false,
      url: ROUTES.dashboard,
      visible: true
    },
    {
      text: "Finanziamenti",
      active: false,
      external: false,
      url: "/futuro",
      visible: true
    }
  ]

  return (
    <Suspense fallback={<Loader fullPage />}>
      <>
        <SkipLink />
        <Menu user={user} secondaryMenu={secondaryMenu} primaryMenu={primaryMenu} />
        {isDashboardEnabled ? (
          <>
            {/* Privacy policy for partner's clients */}
            <PrivacyPolicy />
            <GdprConsents email={user.email} />
            <PageContainer>{children(user)}</PageContainer>
          </>
        ) : (
          <UserNotAllowed />
        )}
        <Footer />
      </>
    </Suspense>
  )
}
